import React from 'react';
import { FaShieldAlt, FaMicrophone, FaDesktop, FaCheck, FaCog, FaRedo } from 'react-icons/fa';
import Button from '../buttons/Button';
import './PermissionModal.css';

/**
 * PermissionModal - Modal shown at startup when permissions are missing
 *
 * @param {Object} props
 * @param {Object} props.permissions - Object with permission status { accessibility, microphone, screenCapture }
 * @param {function} props.onOpenSettings - Called with permission type ('accessibility' | 'microphone' | 'screenCapture')
 * @param {function} props.onRestartApp - Called when user clicks restart
 * @param {function} props.onDismiss - Called when user dismisses the modal
 */
const PermissionModal = ({
  permissions = {},
  onOpenSettings,
  onRestartApp,
  onDismiss
}) => {
  const isMac = permissions.platform === 'darwin';

  const permissionItems = isMac ? [
    {
      key: 'microphone',
      icon: FaMicrophone,
      label: 'Microphone',
      description: 'Pour la dictée vocale',
      granted: permissions.microphone
    },
    {
      key: 'accessibility',
      icon: FaShieldAlt,
      label: 'Accessibilité',
      description: 'Pour l\'insertion de texte',
      granted: permissions.accessibility
    },
    {
      key: 'screenCapture',
      icon: FaDesktop,
      label: 'Enregistrement d\'écran',
      description: 'Pour la capture d\'écran',
      granted: permissions.screenCapture
    }
  ] : [];

  const missingPermissions = permissionItems.filter(p => !p.granted);
  const allGranted = missingPermissions.length === 0;

  if (allGranted) return null;

  const handleOpenSettings = (type) => {
    if (onOpenSettings) {
      onOpenSettings(type);
    }
  };

  return (
    <div className="startup-permission-overlay">
      <div className="startup-permission-modal">
        <div className="startup-permission-header">
          <h2>Autorisations requises</h2>
          <p>Voxia a besoin de certaines autorisations pour fonctionner correctement.</p>
        </div>

        <div className="startup-permission-list">
          {permissionItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <div
                key={item.key}
                className={`startup-permission-item ${item.granted ? 'granted' : 'missing'}`}
              >
                <div className="permission-item-icon">
                  <IconComponent />
                </div>
                <div className="permission-item-info">
                  <span className="permission-item-label">{item.label}</span>
                  <span className="permission-item-desc">{item.description}</span>
                </div>
                <div className="permission-item-status">
                  {item.granted ? (
                    <FaCheck className="status-granted" />
                  ) : (
                    <button
                      className="permission-settings-btn"
                      onClick={() => handleOpenSettings(item.key)}
                    >
                      <FaCog />
                      <span>Activer</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="startup-permission-footer">
          <p className="permission-hint">
            Après avoir activé les autorisations, redémarrez l'application.
          </p>
          <div className="startup-permission-actions">
            <Button
              variant="primary"
              onClick={onRestartApp}
              icon={FaRedo}
            >
              Redémarrer Voxia
            </Button>
            {onDismiss && (
              <Button
                variant="secondary"
                onClick={onDismiss}
              >
                Plus tard
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionModal;
